<template>
    <label class="input-field file-input">
        <header class="label">
            <slot name="header" :label="documentSide">
                <span v-html="documentSide" />
            </slot>
        </header>

        <main class="input uploader" :class="{ invalid: fileAlreadySelected || fileTypeIncorrect, active: dropping }">
            <input
                v-if="!readonly"
                type="file"
                ref="fileInputElement"
                :id="`${upload.name}-${upload.type}`"
                :name="`${upload.name}-${upload.type}`"
                :accept="uploadTypes.join(',')"
                @change="selectedFile"
            />

            <transitional name="fade" mode="out-in">
                <file-preview v-if="!!upload.base64" :upload="upload" @removed="removeUpload" />

                <template v-else>
                    <file-drop-zone v-if="!readonly" class="upload" @updated="droppedFiles" @focused="dropping = true" @blurred="dropping = false">
                        <div>
                            <upload-icon />
                        </div>
                        <span class="uppercase-first" v-html="uploadDocumentSide" />
                    </file-drop-zone>

                    <slot v-else :description="uploadDocumentSide" />
                </template>
            </transitional>
        </main>

        <strong v-if="fileTypeIncorrect" class="validation-error" v-html="t(`widget.${translationDomain}.field-error.upload-file-type`)" />
        <strong v-if="fileAlreadySelected" class="validation-error" v-html="t(`widget.${translationDomain}.field-error.upload-already-exists`)" />

        <slot name="custom-error" />
    </label>
</template>

<script lang="ts" setup>
import { UploadedFile, Uploads } from '@btcdirect/components/file-field/types';
import { useTranslations } from '@btcdirect/composables';
import Transitional from '@btcdirect/shared/src/components/transition/transitional.vue';
import { EmitEvents } from '@btcdirect/shared/src/components/types';
import { computed, reactive, ref, watch } from 'vue';
import UploadIcon from '../icons/upload.vue';
import FileDropZone from './file-drop-zone.vue';
import FilePreview from './file-preview.vue';

const emit = defineEmits([EmitEvents.UPDATED, EmitEvents.REMOVED]);

const props = defineProps<{
    name: string;
    type: string;
    translationDomain: string;
    selected?: Uploads;
    readonly?: boolean;
}>();

const { t } = useTranslations();

const dropping = ref<boolean>(false);

const fileInputElement = ref<HTMLInputElement>(null);

const upload = reactive<UploadedFile>({
    name: props.name,
    type: props.type,
    file: null,
    base64: null
});

// TODO: Expand to more file types?
const uploadTypes = ['image/apng', 'image/heic', 'image/heif', 'image/jpeg', 'image/jpg', 'image/png'];

const selectedFile = (): void => {
    const files = fileInputElement.value.files;
    if (!files.length) {
        return;
    }

    const file = files[0];
    processFile(file);
};

const droppedFiles = (files: File[]) => {
    const file = files[0];
    processFile(file);
};

const fileAlreadySelected = ref<boolean>(false);
const fileTypeIncorrect = ref<boolean>(false);

const processFile = (file: File) => {
    fileAlreadySelected.value = isFileAlreadySelected(file);
    if (fileAlreadySelected.value) {
        return;
    }

    fileTypeIncorrect.value = !uploadTypes.includes(file.type);
    if (fileTypeIncorrect.value) {
        return;
    }

    upload.file = file;

    const reader = new FileReader();
    reader.onload = () => {
        upload.base64 = reader.result as string;
        emitUpdate();
    };
    reader.readAsDataURL(file);
};

const removeUpload = (): void => {
    upload.base64 = null;
    upload.file = null;
    if (!!fileInputElement.value) {
        fileInputElement.value.value = '';
    }

    emitUpdate();
    emit(EmitEvents.REMOVED);
};

const emitUpdate = () => {
    const data: UploadedFile = {
        name: upload.name,
        type: upload.type,
        file: upload.file,
        base64: upload.base64
    };

    emit(EmitEvents.UPDATED, data);
};

const documentSide = computed<string>(() => t(`widget.${props.translationDomain}.document-upload.${props.type.toLowerCase()}.label`));
const uploadDocumentSide = computed<string>(() => t(`widget.${props.translationDomain}.document-upload.upload`, { side: documentSide.value.toLowerCase() }));

function isFileAlreadySelected(file: File): boolean {
    return (
        props.selected &&
        Object.entries(props.selected).some(
            (value) => value[0] !== props.type && value[1].file?.name === file.name && value[1].file?.lastModified === file.lastModified
        )
    );
}

watch(
    () => props.selected,
    () => {
        const selectedFile: UploadedFile = props.selected[props.type];
        if (!selectedFile) {
            return;
        }
        upload.base64 = selectedFile.base64;
    }
);
</script>

<style lang="scss" scoped>
@use '@@scss/flex';
@use '@@scss/transitions';

.input-field.file-input {
    main.input.uploader {
        @include flex.flex;
        @include flex.align($vertical: center);
        @include transitions.transition((border-color, background-color));
        border-style: dashed;
        height: 5.25rem;

        &.active {
            border-color: var(--btcdirect-blue);
            background-color: var(--ootv-input-background-color);
        }

        &.invalid {
            border-color: var(--ootv-input-error-border-color);
        }

        input {
            display: none;
        }

        .upload,
        .preview {
            @include flex.resize(1);
        }

        .upload {
            @include flex.flex($direction: column);
            @include flex.align(center, center);

            svg {
                margin-bottom: 0.75em;
            }
        }
    }

    strong.validation-error {
        font-size: 0.875em;
        line-height: 2em;
    }
}
</style>
